/**
 * Notes shown in the text (decision 76; part 19e log, table MK): each open note found in the chapter drawn as a mark over
 * its passage, the writer's private ones and others' (an editor's, Word's) apart, following the writer's edits until the
 * app finds the notes again (table AR, off the keystroke path: principle 8). The finding itself never runs here. Colours
 * are the app's: the marks carry classes only, and are never controls (MK5).
 */
import { StateEffect, StateField } from '@codemirror/state';
import type { EditorState } from '@codemirror/state';
import { Decoration, EditorView } from '@codemirror/view';
import type { DecorationSet } from '@codemirror/view';

/** MK1, MK3: the class a note's mark carries: the writer's private note's, or another's. */
export const NOTE_MARK_CLASS = Object.freeze({ private: 'ct-note-private', editor: 'ct-note-editor' });

/** A note's passage in the text, and whose note it is. */
export interface NoteMark {
  readonly from: number;
  readonly to: number;
  readonly kind: keyof typeof NOTE_MARK_CLASS;
}

const marks = Object.freeze({ private: Decoration.mark({ class: NOTE_MARK_CLASS.private }), editor: Decoration.mark({ class: NOTE_MARK_CLASS.editor }) });

/** MK1–MK4: the marks to show now, in the text after the transaction; each replaces every earlier one. */
export const showNoteMarks = StateEffect.define<readonly NoteMark[]>();

/** The marks of `shown`, clipped to a text of `length`; a mark that holds no text (a point) is none. */
function marksOf(shown: readonly NoteMark[], length: number): DecorationSet {
  const ranges = [];
  for (const mark of shown) {
    const from = Math.max(0, mark.from);
    const to = Math.min(length, mark.to);
    if (from < to) ranges.push(marks[mark.kind].range(from, to));
  }
  return Decoration.set(ranges, true);
}

/** AR7: the notes' marks, following the writer's edits until the next {@link showNoteMarks}. */
export const noteMarkField: StateField<DecorationSet> = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update: (value, transaction) => {
    let next = transaction.docChanged ? value.map(transaction.changes) : value;
    for (const effect of transaction.effects) if (effect.is(showNoteMarks)) next = marksOf(effect.value, transaction.state.doc.length);
    return next;
  },
  provide: (field) => EditorView.decorations.from(field),
});

/** The marks shown in `state`, mapped through every edit since, in the text's order. */
export function shownNoteMarks(state: EditorState): NoteMark[] {
  const out: NoteMark[] = [];
  state.field(noteMarkField).between(0, state.doc.length, (from, to, decoration) => {
    out.push({ from, to, kind: decoration === marks.editor ? 'editor' : 'private' });
  });
  // Overlapping marks sit in layers of their own, which `between` visits in turn.
  return out.sort((a, b) => a.from - b.from || a.to - b.to);
}
